import { useEffect, useRef, useState } from 'react';
import { Camera, Upload, RefreshCw, AlertCircle, CircleCheck, X } from 'lucide-react';

export type CameraShape = 'card' | 'face';

interface CameraCaptureProps {
  shape: CameraShape;
  facingMode: 'environment' | 'user';
  onCapture: (file: File, previewUrl: string) => void;
  onCancel?: () => void;
}

// Emirates ID aspect ratio (~credit card). Used for the guide overlay.
const CARD_ASPECT = 1.586;

export default function CameraCapture({ shape, facingMode, onCapture, onCancel }: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [status, setStatus] = useState<'starting' | 'ready' | 'denied' | 'unavailable'>('starting');
  const [quality, setQuality] = useState<'dark' | 'bright' | 'ok'>('ok');

  // --- Start / stop camera -------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus('unavailable');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facingMode },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setStatus('ready');
        loopQuality();
      } catch (err) {
        console.warn('Camera denied / failed:', err);
        setStatus('denied');
      }
    };

    start();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [facingMode]);

  const stopStream = () => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  // --- Simple brightness check, sampled every ~400ms -----------------------
  const loopQuality = () => {
    let last = 0;
    const tick = (ts: number) => {
      rafRef.current = requestAnimationFrame(tick);
      if (ts - last < 400) return;
      last = ts;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || video.readyState < 2) return;

      const w = 32;
      const h = 32;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, w, h);
      const { data } = ctx.getImageData(0, 0, w, h);
      let sum = 0;
      for (let i = 0; i < data.length; i += 4) {
        sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      }
      const avg = sum / (data.length / 4);
      setQuality(avg < 55 ? 'dark' : avg > 235 ? 'bright' : 'ok');
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  // --- Capture -------------------------------------------------------------
  const capture = async () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return;

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const canvas = document.createElement('canvas');
    canvas.width = vw;
    canvas.height = vh;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, vw, vh);

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.9),
    );
    if (!blob) return;
    const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
    const url = URL.createObjectURL(blob);
    stopStream();
    onCapture(file, url);
  };

  // --- Fallback upload -----------------------------------------------------
  const onFallbackFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const url = URL.createObjectURL(f);
    onCapture(f, url);
  };

  // --- Render --------------------------------------------------------------
  return (
    <div className="fixed inset-0 z-50 bg-black text-white flex flex-col">
      <div className="absolute top-0 inset-x-0 p-4 flex items-center justify-between z-20">
        <button
          onClick={() => { stopStream(); onCancel?.(); }}
          className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center"
          aria-label="Close camera"
        >
          <X className="w-5 h-5" />
        </button>
        <QualityBadge quality={quality} status={status} />
      </div>

      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        playsInline
        muted
      />
      <canvas ref={canvasRef} className="hidden" />

      {/* Overlay mask with a cut-out guide */}
      {status === 'ready' && <GuideOverlay shape={shape} />}

      {/* Coaching text */}
      <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 pointer-events-none flex justify-center">
        <div
          className="mt-[180px] bg-black/50 backdrop-blur rounded-full px-4 py-2 text-sm"
          style={{ maxWidth: '80%' }}
        >
          {status === 'starting' && 'Starting camera…'}
          {status === 'ready' && shape === 'card' && 'Fit the card inside the frame'}
          {status === 'ready' && shape === 'face' && 'Centre your face in the oval'}
          {status === 'denied' && 'Camera permission denied'}
          {status === 'unavailable' && 'Camera not available on this device'}
        </div>
      </div>

      <div className="absolute bottom-0 inset-x-0 p-6 z-20 flex items-center justify-center gap-6">
        {status === 'ready' ? (
          <button
            onClick={capture}
            className="w-20 h-20 rounded-full bg-white ring-4 ring-white/40 hover:ring-white/60 transition-all flex items-center justify-center"
            aria-label="Capture"
          >
            <div className="w-16 h-16 rounded-full bg-white border-4 border-black/10" />
          </button>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="h-12 px-6 rounded-xl bg-white text-black flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Upload a photo instead
            </button>
            {(status === 'denied' || status === 'unavailable') && (
              <p className="text-xs text-white/70 max-w-xs text-center">
                Enable camera access in your browser, or upload a photo from your device.
              </p>
            )}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic"
          className="hidden"
          onChange={onFallbackFile}
        />
      </div>
    </div>
  );
}

// --- Overlay: dark mask with a transparent guide ---------------------------
function GuideOverlay({ shape }: { shape: CameraShape }) {
  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none z-10"
      preserveAspectRatio="xMidYMid slice"
      viewBox="0 0 100 100"
    >
      <defs>
        <mask id="camera-mask">
          <rect x="0" y="0" width="100" height="100" fill="white" />
          {shape === 'card' ? (
            <rect
              x={(100 - 80) / 2}
              y={50 - (80 / CARD_ASPECT) / 2}
              width="80"
              height={80 / CARD_ASPECT}
              rx="3"
              fill="black"
            />
          ) : (
            <ellipse cx="50" cy="46" rx="26" ry="34" fill="black" />
          )}
        </mask>
      </defs>
      <rect x="0" y="0" width="100" height="100" fill="rgba(0,0,0,0.55)" mask="url(#camera-mask)" />
      {/* Stroke the guide so the edge is always visible */}
      {shape === 'card' ? (
        <rect
          x={(100 - 80) / 2}
          y={50 - (80 / CARD_ASPECT) / 2}
          width="80"
          height={80 / CARD_ASPECT}
          rx="3"
          fill="none"
          stroke="#D4F04A"
          strokeWidth="0.5"
        />
      ) : (
        <ellipse
          cx="50" cy="46" rx="26" ry="34"
          fill="none" stroke="#D4F04A" strokeWidth="0.5"
        />
      )}
    </svg>
  );
}

function QualityBadge({
  quality,
  status,
}: {
  quality: 'dark' | 'bright' | 'ok';
  status: 'starting' | 'ready' | 'denied' | 'unavailable';
}) {
  if (status !== 'ready') return <span />;
  const good = quality === 'ok';
  const label = good ? 'Good lighting' : quality === 'dark' ? 'Too dark' : 'Too bright';
  const Icon = good ? CircleCheck : AlertCircle;
  return (
    <div
      className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs backdrop-blur ${
        good ? 'bg-[#D4F04A]/90 text-[#14563F]' : 'bg-amber-400/90 text-amber-950'
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </div>
  );
}
